import { useEffect, useRef, useState } from 'react';
import useGardenLoader from '../hooks/useGardenLoader';
import OfflineEmptyScreen from './ErrorStates/OfflineEmptyScreen';

const ACTIVE_GARDEN_ID_KEY = 'gartenplaner_active_garden_id';

/**
 * GardenStartupLoader — single backend-authoritative garden load on app startup.
 *
 * Mounted once inside the full provider tree (below PlantProvider) so it can use
 * useGardenLoader, which hydrates GardenSettingsContext (→ isConfigured),
 * DashboardContext.placedPlants, canvasObjects, favorites and the wizard data.
 *
 * Before #484 this load only ran when the editor (MainLayout) mounted, so the
 * Dashboard — the first view after login — seeded its plant state purely from
 * the localStorage `gartenplaner_canvas` cache. On a fresh device / after a
 * cache clear that cache is empty, so a garden with backend plants wrongly
 * showed State 0. Hoisting the load here makes the backend the source of truth
 * for both Dashboard and Editor; localStorage is only an offline cache.
 *
 * #570 (Stufe 1): the load path now also reads from the IndexedDB offline cache
 * (useGardenLoader). When the cold-start load fails *offline* with no cached
 * copy (cache miss → re-throw) we render the full-screen OfflineEmptyScreen
 * instead of letting the Dashboard show a misleading empty State 0. An online
 * failure keeps the previous silent behaviour (localStorage fallback / normal
 * error states), since a full-screen replace would hide usable content (#491).
 */
export default function GardenStartupLoader() {
  const { loadFromBackend } = useGardenLoader();
  const ran = useRef(false);
  const [coldLoadFailedOffline, setColdLoadFailedOffline] = useState(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const storedId = localStorage.getItem(ACTIVE_GARDEN_ID_KEY);
    if (!storedId) return;

    loadFromBackend(storedId).catch(() => {
      // The loader already tried the IndexedDB cache and re-threw on a miss.
      // If we're offline, there is genuinely nothing to show → full-screen
      // OfflineEmptyScreen. If we're online, this is a server/network error the
      // Dashboard/Editor handle via their localStorage fallback.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setColdLoadFailedOffline(true);
      }
    });
  }, [loadFromBackend]);

  if (coldLoadFailedOffline) {
    return (
      <div className="fixed inset-0 z-50 bg-white overflow-auto">
        <OfflineEmptyScreen onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return null;
}
